export interface HeroContent {
  badge: string;
  headline1: string;
  headline2: string;
  subtext: string;
  cta1: string;
  cta2: string;
  imageDark: string;
  imageLight: string;
}

export interface AboutContent {
  badge: string;
  headline1: string;
  headline2: string;
  paragraph1: string;
  paragraph2: string;
  ctaLabel: string;
}

export interface StatItem {
  value: string;
  label: string;
  sub: string;
}

export interface FeatureItem {
  icon: string;
  title: string;
  desc: string;
}

export interface TestimonialItem {
  name: string;
  role: string;
  text: string;
  initials: string;
}

export interface FooterContent {
  phone: string;
  email: string;
  copyright: string;
  socials: {
    facebook: string;
    instagram: string;
    youtube: string;
    linkedin: string;
  };
}

export interface AnimationCard {
  enabled: boolean;
  icon: string;
  title: string;
  desc: string;
}

export type AnimationMediaMode = 'animation' | 'image' | 'video';

export interface AnimationSection {
  enabled: boolean;
  animationEnabled: boolean;
  cardsEnabled: boolean;
  mediaMode: AnimationMediaMode;
  mediaUrl: string;
  badge: string;
  headline1: string;
  headline2: string;
  subtitle: string;
  cards: AnimationCard[];
}

export interface SiteContent {
  hero: HeroContent;
  about: AboutContent;
  stats: StatItem[];
  features: FeatureItem[];
  testimonials: TestimonialItem[];
  footer: FooterContent;
  animation: AnimationSection;
}

export const defaultSiteContent: SiteContent = {
  hero: {
    badge: 'Global Marine Brand',
    headline1: 'POWER THAT',
    headline2: 'NEVER QUITS.',
    subtext: 'Stingray marine batteries are engineered for the harshest saltwater environments worldwide. Lithium, AGM, and Gel — built to keep you on the water longer, wherever you sail.',
    cta1: 'Explore Products',
    cta2: 'About Technology',
    imageDark: 'https://readdy.ai/api/search-image?query=dramatic%20ocean%20marine%20boat%20on%20open%20water%20at%20golden%20hour%20sunset%20dark%20moody%20atmosphere%20deep%20navy%20blue%20water%20with%20orange%20horizon%20glow%20cinematic%20wide%20angle%20shot%20professional%20photography%20high%20contrast%20dramatic%20lighting%20no%20people%20empty%20boat%20deck%20view&width=1440&height=900&seq=hero-bg-main&orientation=landscape',
    imageLight: 'https://readdy.ai/api/search-image?query=bright%20sunny%20day%20calm%20turquoise%20ocean%20water%20aerial%20view%20luxury%20yacht%20sailing%20clear%20blue%20sky%20white%20clouds%20tropical%20paradise%20pristine%20sea%20surface%20sparkling%20sunlight%20fresh%20clean%20marine%20environment%20wide%20angle&width=1440&height=900&seq=hero-bg-light&orientation=landscape',
  },
  about: {
    badge: 'Global Marine Brand',
    headline1: 'Built for the Water.',
    headline2: 'Trusted Worldwide.',
    paragraph1: 'Stingray is headquartered in Sheridan, Wyoming, with a global reach. We design and source premium marine batteries that meet the demanding standards of boaters and commercial operators around the world.',
    paragraph2: 'Every Stingray battery is engineered to our exact specifications and rigorously tested before it reaches your vessel. From weekend anglers to offshore professionals, we power the boats that matter most — on every ocean.',
    ctaLabel: 'Our Story',
  },
  stats: [
    { value: '10Y', label: 'Design Life', sub: 'LiFePO4 Series' },
    { value: '2000+', label: 'Charge Cycles', sub: 'Lithium batteries' },
    { value: '50+', label: 'Countries', sub: 'Global distribution' },
    { value: 'IP67', label: 'Waterproof', sub: 'Marine certified' },
  ],
  features: [
    { icon: 'ri-shield-check-line', title: 'Marine Grade', desc: 'IP67 waterproof & corrosion resistant' },
    { icon: 'ri-flashlight-line', title: 'High Performance', desc: 'Up to 2000+ charge cycles' },
    { icon: 'ri-leaf-line', title: 'Eco Friendly', desc: 'Non-toxic LiFePO4 chemistry' },
    { icon: 'ri-customer-service-2-line', title: '5-Year Warranty', desc: 'Industry-leading coverage' },
  ],
  testimonials: [
    { name: 'Captain Mike Harrington', role: 'Charter Fishing, Florida Keys, USA', text: 'Switched my entire fleet to Stingray LiFePO4 last season. The weight savings alone changed how my boats handle. These batteries are the real deal.', initials: 'MH' },
    { name: 'Lars Eriksson', role: 'Offshore Sailor, Stockholm, Sweden', text: 'We run Stingray lithiums on our 42ft cruiser across the North Sea. Reliable in extreme cold, zero maintenance issues. Exceptional quality for demanding conditions.', initials: 'LE' },
    { name: 'Sarah Nguyen', role: 'Yacht Owner, Sydney, Australia', text: 'The AGM series is perfect for our vessel. Maintenance-free, reliable, and the price point is excellent. Stingray customer service was also top notch.', initials: 'SN' },
    { name: 'Marco Ferretti', role: 'Marine Electronics Installer, Genoa, Italy', text: 'I recommend Stingray Gel batteries to all my customers with sensitive electronics. The stable voltage output is unmatched at this price point.', initials: 'MF' },
    { name: 'Rick Castellano', role: 'Offshore Sportfisher, New Jersey, USA', text: 'Running 200Ah Stingray lithiums on my 35-footer. Replaced three AGM batteries with one Stingray and gained 150 lbs of weight savings. Incredible.', initials: 'RC' },
    { name: 'James Whitmore', role: 'Marine Dealer, Dubai, UAE', text: 'As a dealer in the Gulf region, Stingray is the easiest sell in my showroom. Customers come back happy every time. The dealer portal makes ordering a breeze.', initials: 'JW' },
  ],
  footer: {
    phone: '+1 307 381 5172',
    email: 'info@stingrayenergy.net',
    copyright: 'Stingray — Sheridan, WY 82801, USA',
    socials: {
      facebook: 'https://facebook.com',
      instagram: 'https://instagram.com',
      youtube: 'https://youtube.com',
      linkedin: 'https://linkedin.com',
    },
  },
  animation: {
    enabled: true,
    animationEnabled: true,
    cardsEnabled: true,
    mediaMode: 'animation',
    mediaUrl: '',
    badge: 'The Stingray Story',
    headline1: "Born from the Ocean's",
    headline2: "Most Electric Creature",
    subtitle: "The stingray is nature's master of electric power — silent, efficient, and unstoppable. That's exactly what we built into every battery.",
    cards: [
      { enabled: true, icon: 'ri-flashlight-line', title: 'Electric by Nature', desc: "Stingrays generate bioelectric fields to navigate and hunt — we channel that same energy into marine power." },
      { enabled: true, icon: 'ri-water-flash-line', title: 'Built for the Deep', desc: 'IP67 waterproof, corrosion-resistant, engineered to thrive in the harshest saltwater environments.' },
      { enabled: true, icon: 'ri-battery-charge-line', title: '2,000+ Cycles', desc: "Like the stingray's endurance, our LiFePO4 batteries outlast the competition by 5x." },
    ],
  },
};
